import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Power, PowerOff, Search } from 'lucide-react';
import { toast } from 'sonner';
import { resolveHomePath, useAuth } from '@/shared/lib/auth';
import { isMasterEmail } from '@/shared/lib/navigation';
import type { PanelUser, Role } from '@/shared/types';
import { useSelection } from '@/shared/lib/use-selection';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Pagination } from '@/shared/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { FloatingActionBar } from '@/shared/components/floating-action-bar';
import { useConfirm } from '@/shared/components/confirm';
import { useUpdateAiUser } from '@/features/ai-credits/api/use-ai-admin';
import type { AiUserRow } from '@/features/ai-credits/model/types';
import {
  usePassbookStoreOptions,
  usePassbookUsers,
  type PanelUserStatus,
  type PassbookPermissionRow,
} from '@/features/permissions/api/use-permissions';
import { panelRoleLabel } from '../model/roles';
import { UserSheet } from './user-sheet';
import { UsersTable } from './users-table';

const PAGE_SIZE = 20;
const ALL = 'all';

/** Usuários de um passbook (aba do hub do cliente): tabela, filtros e o editor único. */
const ROLES: ReadonlySet<string> = new Set<Role>([
  'Admin', 'Owner', 'Client', 'Manager', 'Operator', 'Integration', 'Validator', 'Campaign', 'Base', 'Nps', 'Employee',
]);

export function PassbookUsersTab({ passbook }: { passbook: PassbookPermissionRow }) {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { realUser, authSource, startViewAs } = useAuth();
  const canViewAs = Boolean(realUser && isMasterEmail(realUser.email));

  // Monta o PanelUser mínimo que menu e guards leem. A sessão continua a do master.
  const viewAs = (row: AiUserRow) => {
    if (!ROLES.has(row.role)) {
      toast.error(`Perfil ${row.role} não dá para simular.`);
      return;
    }
    const target: PanelUser = {
      _id: row.id,
      name: row.name,
      email: row.email,
      role: row.role as Role,
      id_passbooks: row.passbook?.id ?? passbook.id,
      hasCampaignAdvanced: row.hasCampaignAdvanced,
      pageFlags: row.pageFlags,
      stores: row.storeCodes,
    };
    startViewAs(target);
    navigate(resolveHomePath(target, authSource));
  };
  const update = useUpdateAiUser(passbook.id);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState<PanelUserStatus>('all');
  const [role, setRole] = useState(ALL);
  const [storeCode, setStoreCode] = useState(ALL);
  const [page, setPage] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<AiUserRow | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [batchBusy, setBatchBusy] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => setPage(0), [debouncedSearch, status, role, storeCode]);

  const { data: stores } = usePassbookStoreOptions(passbook);
  const { data, isLoading, isFetching } = usePassbookUsers(passbook.id, {
    page,
    limit: PAGE_SIZE,
    search: debouncedSearch,
    status,
    role: role === ALL ? undefined : role,
    storeCode: storeCode === ALL ? undefined : storeCode,
  });

  const rows = useMemo(() => (data?.rows ?? []).map((row) => ({ ...row, _id: row.id })), [data]);
  const roles = useMemo(
    () => [...new Set((data?.roles ?? []).map((value) => String(value).trim()).filter(Boolean))],
    [data?.roles],
  );
  const storeOptions = useMemo(() => (stores?.rows ?? []).filter((store) => store.storeCode.trim()), [stores?.rows]);
  const selection = useSelection(rows);
  const { clear } = selection;
  useEffect(() => clear(), [page, debouncedSearch, status, role, storeCode, clear]);

  const setActive = async (user: AiUserRow, next: boolean) => {
    if (!next) {
      const ok = await confirm({
        title: `Desativar ${user.name}?`,
        description: 'Perde o acesso ao painel. Dá para reativar depois.',
        confirmLabel: 'Desativar',
        variant: 'destructive',
      });
      if (!ok) return;
    }
    setBusyIds((prev) => new Set(prev).add(user.id));
    try {
      const row = await update.mutateAsync({ id: user.id, active: next });
      setEditing((prev) => (prev?.id === row.id ? row : prev));
      toast.success(`${user.name} ${next ? 'ativado' : 'desativado'}.`);
    } catch {
      toast.error('Não deu para alterar o acesso.');
    } finally {
      setBusyIds((prev) => {
        const copy = new Set(prev);
        copy.delete(user.id);
        return copy;
      });
    }
  };

  const batchSetActive = async (next: boolean) => {
    const targets = selection.selected.filter((user) => user.active !== next && !isMasterEmail(user.email));
    if (targets.length === 0) {
      toast.info(next ? 'Já estão ativos.' : 'Já estão inativos.');
      return;
    }
    if (!next) {
      const ok = await confirm({
        title: `Desativar ${targets.length} usuário(s)?`,
        description: 'Perdem o acesso ao painel até serem reativados.',
        confirmLabel: 'Desativar',
        variant: 'destructive',
      });
      if (!ok) return;
    }
    setBatchBusy(true);
    let ok = 0;
    for (const user of targets) {
      try {
        await update.mutateAsync({ id: user.id, active: next });
        ok += 1;
      } catch {
        /* continua com os demais */
      }
    }
    setBatchBusy(false);
    if (ok > 0) {
      toast.success(`${ok} ${next ? 'ativado(s)' : 'desativado(s)'}.`);
      selection.clear();
    } else {
      toast.error('Nada alterado.');
    }
  };

  const openUser = (user: AiUserRow | null) => {
    setEditing(user);
    setSheetOpen(true);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nome ou e-mail" className="pl-9" />
        </div>
        <Select value={status} onValueChange={(v) => setStatus(v as PanelUserStatus)}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Situação" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Perfil" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os perfis</SelectItem>
            {roles.map((value) => (
              <SelectItem key={value} value={value}>
                {panelRoleLabel(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {passbook.storeCount > 0 ? (
          <Select value={storeCode} onValueChange={setStoreCode}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Loja" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todas as lojas</SelectItem>
              {storeOptions.map((store) => (
                <SelectItem key={store.id} value={store.storeCode}>
                  {store.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        <Button type="button" className="ml-auto" onClick={() => openUser(null)}>
          <Plus className="size-4" /> Novo usuário
        </Button>
      </div>

      <UsersTable
        rows={rows}
        loading={isLoading}
        fetching={isFetching}
        storeCatalog={storeOptions}
        selection={selection}
        onOpen={openUser}
        onViewAs={canViewAs ? viewAs : undefined}
        onToggleActive={(user, next) => void setActive(user, next)}
        busyIds={busyIds}
        emptyTitle="Nenhum usuário"
        emptyDescription={debouncedSearch || status !== 'all' || role !== ALL ? 'Ajuste os filtros.' : 'Cadastre o primeiro usuário deste cliente.'}
        footer={
          (data?.total ?? 0) > PAGE_SIZE ? (
            <Pagination page={page} pageSize={PAGE_SIZE} total={data?.total ?? 0} onPageChange={setPage} />
          ) : null
        }
      />

      <FloatingActionBar count={selection.selected.length} onClear={selection.clear}>
        <Button variant="secondary" size="sm" onClick={() => void batchSetActive(true)} loading={batchBusy}>
          <Power className="size-4" /> Ativar
        </Button>
        <Button variant="destructive" size="sm" onClick={() => void batchSetActive(false)} loading={batchBusy}>
          <PowerOff className="size-4" /> Desativar
        </Button>
      </FloatingActionBar>

      <UserSheet
        open={sheetOpen}
        user={editing}
        passbookId={passbook.id}
        onClose={() => setSheetOpen(false)}
        onSaved={setEditing}
      />
    </div>
  );
}
